import AccessMixin from '@/components/Base/AccessMixin';
import { levelProUser } from '@/store/user';

/**
 * Mixin for editable resources.
 */
export default {

    mixins: [ AccessMixin ],

    data: function () {
        return {
            resource: '', // artworks, collections, actors, ...

            editMode: false,
            createMode: false,
            previewMode: false,
            doEdit: false, // editMode && canEdit

            accessGroups: {},

            originalValues: {},
            payloadFields: [
                'Locale',
                'Name',
                'Editors',
                'BodyHtml',
                'IngressText',
                'TopicDate',

                // Organizations
                'TagLine',
                'LocalityArea',
                'PublicLocation',

                // Actors
                'ArtistStatementHtml',
                'BiographyHtml',
                'CollectionsHtml',
                'EducationHtml',
                'FamilyHtml',
                'MembershipsHtml',
                'PrizesHtml',
                'PublicWorksHtml',
                'SourcesHtml',
                'BirthDate',
                'DeathDate',
                'ActorType',

                // Collections
                'DescriptionHtml',
                'HistoryHtml',
                'ExhibitionsHtml',
                'ResearchHtml',
                'NumberOfWorks',
                'PolicyHtml',
                'PublicationsHtml',

                // FeatureArticle Images
                'Caption',
                'CreditLine',

                // Events
                'Start',
                'End',
                'EventClass',
                'EntryDescription',
                'OrganizerId'
            ]
        };
    },

    mounted: function () {
        this.$eventBus.$on('edit-mode-change', this.changeEditMode);
    },

    beforeDestroyed: function () {
        this.$eventBus.$off('edit-mode-change', this.changeEditMode);
    },

    watch: {
        /**
         * Check page editability on `item` change.
         */
        item: function () {
            this.initEditableView();
        },

        editMode: function () {
            this.initEditMode();
        },

        // createMode: function () {
        //     this.initCreateMode();
        // },

        previewMode: function () {
            this.initEditMode();
        }
    },

    /**
     * Called before route changes to check and alert for unsaved changes.
     */
    beforeRouteLeave: function (to, from, next) {
        if (this.doEdit) {
            this.prepareToExit(next);
        }
        else {
            next();
        }
    },

    methods: {
        /**
         * Editable view init.
         * TODO: detect parent publish rights in createMode
         */
        initEditableView: function () {
            if (!this.item) {
                return;
            }

            this.getAccessPrivileges(this.item); // from AccessMixin

            // check for user access, do not load edit mode if not Pro User
            if (this.$store.getters['user/getMembershipLevel'] < levelProUser) {
                this.accessGroups = {};
                this.createMode = false;
                this.editMode = false;
                this.doEdit = false;
                return;
            }

            // disable doEdit flag without edit access
            if (!this.canEdit && !this.createMode) {
                this.doEdit = false;
                this.editMode = false;
                this.createMode = false;
                return;
            }

            this.editMode = this.$store.getters['ux/getEditMode'];
            this.createMode = (this.item && this.item.Id && this.item.Id == 0) || false; // eslint-disable-line eqeqeq
            this.accessGroups = this.getAccessGroups(this.item); // from AccessMixin

            if (this.createMode) {
                this.initCreateMode();
            }
            else if (this.editMode) {
                this.initEditMode();
            }
        },

        /**
         * Detect if page is editable.
         */
        initEditMode: function () {
            if (!this.canEdit || this.previewMode) {
                this.doEdit = false;
            }
            else {
                this.doEdit = (this.editMode || this.createMode);
                this.setCurrentEditValues();
            }
        },

        /**
         */
        initCreateMode: function () {
            this.initEditMode();
            this.doEdit = true;
            this.canPublish = true;
        },

        /**
         * Callback for eventbus event `edit-mode-changed`.
         */
        changeEditMode: function (mode) {
            if (this.editMode) {
                this.cancelEdit();
            }
            this.editMode = mode;
        },

        /**
         * Check and alert for unsaved changes before leaving the page.
         */
        prepareToExit: function (next) {
            if (this.hasUnsavedEdits()) {
                this.$eventBus.$emit('unsaved-changes-warning', next);
                return;
            }
            next();
        },

        /**
         * Check the vuex sessionStore for unsaved edits.
         * These are commited to the store from EditableHtmlSection.
         */
        hasUnsavedEdits: function () {
            return this.$store.getters['ux/getHasUnsavedEdits'];
        },

        /**
         * Save editable item values into memory for undo.
         * NOTE: Only if item is a data item, not passed via props.
         */
        setCurrentEditValues: function () {
            if (this._data.item) {
                this.originalValues = this.getPayload();
                this.setDefaultEditor();
            }
            else {
                this.originalValues = {};
            }
            if (this.hasUnsavedEdits()) {
                this.$store.commit('ux/setHasUnsavedEdits', false);
            }
        },

        /**
         * Get current Editor.
         */
        getCurrentEditor: function () {
            const profile = this.$store.getters['user/getProfile'];
            const name = (profile && profile.DisplayName) || '';
            return {
                DateEdited: new Date().toISOString(),
                EditorName: name,
                HideName: true
            };
        },

        /**
         * If item has no Editors, add one.
         */
        setDefaultEditor: function () {
            if (this.item && !this.item.Editors) {
                this.item.Editors = [];
            }
            if (this.item && !this.item.Editors.length && this.doEdit) {
                // set default editor
                this.item.Editors.push(this.getCurrentEditor());
            }
        },

        togglePreview: function () {
            this.previewMode = !this.previewMode;
        },

        /**
         * Cancel edit and undo changes.
         * NOTE: Only if item is a data item, not passed via props.
         */
        cancelEdit: function () {
            if (this._data.item) {
                _.extend(this.item, this.originalValues);
            }
            this.editMode = false;
            if (this.hasUnsavedEdits()) {
                this.$store.commit('ux/setHasUnsavedEdits', false);
            }
        },

        /**
         * PATCH request data
         * Collect all available parameters.
         * Ones that do not apply to this entity are removed from the payload.
         */
        getPayload: function () {
            const payload = {};
            this.payloadFields.forEach((field) => {
                if (typeof this.item[field] !== 'undefined') {
                    const data = this.item[field];
                    payload[field] = data;
                }
            });
            if (!payload.Locale) {
                payload.Locale = this.$i18n.locale;
            }
            return payload;
        },

        /**
         * Resource Access groups for payload.
         */
        getPayloadAccessGroups: function (accessGroups) {
            return _.pickBy(accessGroups, _.identity); // remove keys with no value
        },

        /**
         * Resource save (Artwork, Actor, Organization, etc).
         * With changes in edit mode.
         * Flushes the buffers by emitting `save` signal for html wysiwyg components.
         * Then promises are flushed to get the most recent data into the payload.
         */
        onSave: async function (event) {
            const button = (event && event.target.parentElement) || {};
            if (button && button.disabled) {
                return;
            }
            button.disabled = true; // prevent double click

            // emit event to show dialog
            this.$eventBus.$emit('save');

            // wait for promises to fulfill - flushing editor buffers
            this.$helpers.flushPromises()
                .then(() => {
                    this.patchResource()
                        .then(() => {
                            this.setCurrentEditValues();
                            this.$eventBus.$emit('save-success');
                            try {
                                this.$forceUpdate();
                            }
                            catch (error) {}
                        })
                        .catch((error) => {
                            this.$eventBus.$emit('save-error', error);
                        })
                        .finally(() => {
                            button.disabled = false;
                        });
                });
        },

        /**
         * Update resource.
         * Views may use their own PATCH endpoint and override this method.
         */
        patchResource: async function () {
            return this.$SVC.putResourceArticle(this.resource, this.item.Id, this.getPayload());
        }
    }
};
